import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import {
  currencyLov,
  getClearHeight,
  PropertyType,
  propertyTypeDisplayFnFactory,
  valueWithUomPrefix,
} from '../us.common';
import {
  US_AVAILABILITY_COMMON_BODY_FIELDS,
  US_AVAILABILITY_COMMON_FOOTER_FIELDS,
  US_AVAILABILITY_COMMON_HEADER_FIELDS,
} from './us-availability.common';
import { DASH, formatNumber, formatValueWithUom } from '@miq/utils';
import { digger } from '@cbre/digger';

export const defaultTxt = DASH;
const industrialDisplayFnWrapper = propertyTypeDisplayFnFactory(PropertyType.Industrial);

export const displayFnMap: TemplateFieldsDisplayFn = {
  ...US_AVAILABILITY_COMMON_HEADER_FIELDS(PropertyType.Industrial),
  ...US_AVAILABILITY_COMMON_BODY_FIELDS(PropertyType.Industrial),
  ...US_AVAILABILITY_COMMON_FOOTER_FIELDS,
  205: { displayFn: industrialDisplayFnWrapper }, // 205 - Power (Amps)
  206: { displayFn: industrialDisplayFnWrapper }, // 206 - Power (volts)
  207: { displayFn: industrialDisplayFnWrapper }, // 207 - Yard Space
  208: { displayFn: industrialDisplayFnWrapper }, // 208 - Rail Access
  209: {
    displayFn: (record: ReportRecord, options, opex) => {
      return currencyLov(options).pipe(
        map((currencyLOV: LovValues[]) => {
          const opexVal = valueWithUomPrefix(record, PropertyType.Industrial, opex, currencyLOV);
          return opexVal;
        })
      );
    },
  }, // 209 - OpEx charges
  210: { displayFn: industrialDisplayFnWrapper }, // 210 - Bay Count
  213: { displayFn: industrialDisplayFnWrapper }, // 213 - Bay Depth
  214: { displayFn: industrialDisplayFnWrapper }, // 214 - Bay Width
  211: {
    // 211 - Clear Height
    displayFn: (record: ReportRecord, options, max: DisplayFnArgumentsInfo, min: DisplayFnArgumentsInfo) => {
      const values = getClearHeight(record, options, max, min);
      return values;
    },
  },
  86: {
    // Parking Ratio
    displayFn: industrialDisplayFnWrapper,
  },
  152: {
    // Office Space
    displayFnArguments: [
      {
        id: 3890, // property_measurement_size column ID
        uomId: 3891, // property_measurement_size_uom column ID
      },
    ],
    isTableDataUpdatable: true,
    displayFn: recordData => {
      return getAvailabilityOfficeArea(recordData);
    },
  },
  159: {
    // Sprinkler
    displayFn: industrialDisplayFnWrapper,
  },
  220: {
    // Doors
    displayFn: (record: ReportRecord, _, glDoors, dockDoors) => {
      const glDoorsVal = mapAndFormatDynamicValue(record, glDoors);
      const dockDoorsVal = mapAndFormatDynamicValue(record, dockDoors);
      const result = `${glDoorsVal} GL | ${dockDoorsVal} DH`;
      return result;
    },
  },
};

export const getAvailabilityOfficeArea = record => {
  const recordData = Array.isArray(record?.data) ? record.data[0] : record?.data;
  if (!recordData) {
    return defaultTxt;
  }
  const availableOfficeArea = formatValueWithUom(
    {
      valuePath:
        'property_measurement_availability[property_element_type_desc = "Office Area"][0].property_measurement_size',
      uomPath:
        'property_measurement_availability[property_element_type_desc = "Office Area"][0].property_measurement_size_uom',
      defaultTxt,
      digits: '.0-0',
    },
    recordData
  );

  const totalSpace = digger(recordData, 'available_space');
  const totalSpaceUom = digger(recordData, 'available_space_uom_desc');

  const officeValue = digger(
    recordData,
    'property_measurement_availability[property_element_type_desc = "Office Area"][0].property_measurement_size'
  );
  const officeUom = digger(
    recordData,
    'property_measurement_availability[property_element_type_desc = "Office Area"][0].property_measurement_size_uom'
  );

  // If the UOM do not match do not calculate the percentage and display only the available office area with a dash for the percentage
  if (totalSpaceUom !== officeUom || !totalSpace || totalSpaceUom == null || officeValue == null) {
    return `${availableOfficeArea} | -`;
  }

  const percentageValue = formatNumber((officeValue / totalSpace) * 100, 'en-US', '.0-2');
  return `${availableOfficeArea} | ${percentageValue}%`;
};
